import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { selectProductAttributesByIds } from '@shared/selectors/product.selectors';
import { SharedTrackingService } from '@shared/services/analytics/shared-tracking.service';
import {
  MY_ORDERS_ACTIONS,
  orderExceptionSelectors,
  OrderItemDTOService,
  ORDER_EXCEPTION_ACTIONS,
} from '@usf/ngrx-order';
import { combineLatest, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { submitExceptionsTrace } from './models/submit-exceptions';
import { submitExceptionsFailTrace } from './models/submit-exceptions-fail';
import { submitExceptionsSuccessWebsocketTrace } from './models/submit-exceptions-success-websocket';
import { submitExceptionsSuccessWithExceptionsTrace } from './models/submit-exceptions-success-with-exceptions';
import { submitExceptionsSuccessWithExceptionsWebSocketTrace } from './models/submit-exceptions-success-with-exceptions-websocket';
import { ORDER_EXCEPTIONS_TRACING_ACTIONS } from './order-exceptions-tracing.actions';
import { genericTracingTransformer } from '@shared/tracking/tracing/generic-tracing-transformer';

@Injectable({
  providedIn: 'root',
})
export class OrderExceptionsTracingEffects {
  submitOrderExceptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.resolveOrder),
      map(payload => {
        sessionStorage.setItem('submitOrderExceptionsTrace', 'true');
        const trace = JSON.parse(JSON.stringify(submitExceptionsTrace));
        trace.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trace.tracing.data.attributes.order.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        trace.tracing.transformFunc = genericTracingTransformer;
        return trace;
      }),
      switchMap(tracking => [
        ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptions({ tracking }),
      ]),
    ),
  );

  submitOrderExceptionsSuccess$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderSuccess)),
      this.actions$.pipe(ofType(MY_ORDERS_ACTIONS.getCustomerOrdersSuccess)),
    ]).pipe(
      filter(([payload, customerPayload]) => {
        return (
          sessionStorage.getItem('submitOrderExceptionsTrace') === 'true' &&
          !payload.order.orderHeader.errorDetails &&
          customerPayload?.orders?.filter(
            order =>
              order.orderHeader.orderId === payload.order.orderHeader.orderId &&
              !order.orderHeader.orderExceptions?.length,
          ).length > 0
        );
      }),
      mergeMap(([payload, customerPayload]) => {
        const order = payload?.order;
        const productNumbers = order?.orderItems?.map(orderItem => {
          return orderItem.productNumber;
        });
        return this.store
          .select(selectProductAttributesByIds(productNumbers))
          .pipe(
            take(1),
            map(productMap => {
              const submittedOrderProducts =
                SharedTrackingService.tracingProductData(
                  order?.orderItems,
                  productMap,
                );
              const trace = JSON.parse(
                JSON.stringify(submitExceptionsSuccessWebsocketTrace),
              );
              trace.tracing.data.attributes.order.isWebsocket = false;
              trace.tracing.data.attributes.order.origOrderId =
                order?.orderHeader?.origOrderId;
              trace.tracing.data.attributes.order.origSplitOrderId =
                order?.orderHeader?.origSplitOrderId;
              trace.tracing.data.attributes.order.totalAmount =
                order?.orderHeader?.totalDollars;
              trace.tracing.data.attributes.order.orderId =
                order?.orderHeader?.orderId;
              trace.tracing.data.attributes.order.tandemOrderNumber =
                order?.orderHeader?.tandemOrderNumber;
              trace.tracing.data.attributes.submittedOrderProducts =
                submittedOrderProducts;
              trace.tracing.data.attributes.order.requestedDeliveryDate =
                order?.orderHeader?.requestedDeliveryDate;
              trace.tracing.data.attributes.order.confirmedDeliveryDate =
                order?.orderHeader?.confirmedDeliveryDate;
              trace.tracing.transformFunc = genericTracingTransformer;
              sessionStorage.removeItem('submitOrderExceptionsTrace');
              return trace;
            }),
          );
      }),
      switchMap(tracking => [
        ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptionsSuccessWebSocket({
          tracking,
        }),
      ]),
    ),
  );

  submitOrderExceptionSuccessWebSocket$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderSuccess)),
      this.actions$.pipe(ofType(MY_ORDERS_ACTIONS.updateSubmittedOrderAsync)),
    ]).pipe(
      filter(([payload, webSocketOrder]) => {
        const websocketMsgContentLength =
          webSocketOrder?.orderTransactions.length;

        if (websocketMsgContentLength === 0) {
          return false;
        }
        const newOrder =
          websocketMsgContentLength === 1
            ? webSocketOrder?.orderTransactions[0]
            : webSocketOrder?.orderTransactions[1];

        return (
          sessionStorage.getItem('submitOrderExceptionsTrace') === 'true' &&
          payload.order?.orderHeader?.orderId === newOrder?.orderId &&
          !payload.order.orderHeader.errorDetails
        );
      }),
      mergeMap(([payload, webSocketOrder]) => {
        const productNumbers = payload?.order?.orderItems?.map(orderItem => {
          return orderItem.productNumber;
        });
        return this.store
          .select(selectProductAttributesByIds(productNumbers))
          .pipe(
            take(1),
            map(productMap => {
              const websocketMsgContentLength =
                webSocketOrder?.orderTransactions.length;
              const newOrder =
                websocketMsgContentLength === 1
                  ? webSocketOrder?.orderTransactions[0]
                  : webSocketOrder?.orderTransactions[1];
              const webSocketOrderItems =
                this.orderItemDtoService.toClientModelArray(
                  newOrder?.orderItems,
                );
              const submittedOrderProducts =
                SharedTrackingService.tracingProductData(
                  payload?.order.orderItems,
                  productMap,
                );
              const webSocketOrderProducts =
                SharedTrackingService.tracingProductData(
                  webSocketOrderItems,
                  productMap,
                );
              const trace = JSON.parse(
                JSON.stringify(submitExceptionsSuccessWebsocketTrace),
              );
              trace.tracing.data.attributes.order.isWebsocket = true;
              trace.tracing.data.attributes.order.orderId =
                payload.order.orderHeader.orderId;
              trace.tracing.data.attributes.order.tandemOrderNumber =
                payload.order.orderHeader.tandemOrderNumber;
              trace.tracing.data.attributes.order.requestedDeliveryDate =
                newOrder.requestedDeliveryDate;
              trace.tracing.data.attributes.order.confirmedDeliveryDate =
                newOrder.confirmedDeliveryDate;
              trace.tracing.data.attributes.webSocketOrderProducts =
                webSocketOrderProducts;
              trace.tracing.data.attributes.submittedOrderProducts =
                submittedOrderProducts;
              trace.tracing.data.attributes.order.origOrderId =
                payload.order.orderHeader.origOrderId;
              trace.tracing.data.attributes.order.origSplitOrderId =
                payload.order.orderHeader.origSplitOrderId;
              trace.tracing.data.attributes.order.totalAmount =
                payload.order.orderHeader.totalDollars;
              trace.tracing.transformFunc = genericTracingTransformer;
              sessionStorage.removeItem('submitOrderExceptionsTrace');
              return trace;
            }),
          );
      }),
      switchMap(tracking => [
        ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptionsSuccessWebSocket({
          tracking,
        }),
      ]),
    ),
  );

  submitOrderExceptionsSuccessWithExceptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderSuccess),
      filter(payload => !!payload.order.orderHeader.errorDetails),
      map(payload => {
        const trace = JSON.parse(
          JSON.stringify(submitExceptionsSuccessWithExceptionsTrace),
        );
        trace.tracing.data.attributes.order.orderId =
          payload.order.orderHeader.orderId;
        trace.tracing.data.attributes.order.tandemOrderNumber =
          payload.order.orderHeader.tandemOrderNumber;
        trace.tracing.data.attributes.order.errorDetails =
          payload.order.orderHeader.errorDetails;
        trace.tracing.transformFunc = genericTracingTransformer;
        return trace;
      }),
      switchMap(tracking => [
        ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptionsSuccessWithExceptions(
          {
            tracking,
          },
        ),
      ]),
    ),
  );

  submitOrderExceptionsSuccessWithExceptionsWebSocket$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderSuccess)),
      this.actions$.pipe(ofType(MY_ORDERS_ACTIONS.updateSubmittedOrderAsync)),
    ]).pipe(
      filter(([payload, webSocketOrder]) => {
        const websocketMsgContentLength =
          webSocketOrder?.orderTransactions.length;

        if (websocketMsgContentLength === 0) {
          return false;
        }

        const newOrder =
          websocketMsgContentLength === 1
            ? webSocketOrder?.orderTransactions[0]
            : webSocketOrder?.orderTransactions[1];

        return (
          payload.order.orderHeader.orderId === newOrder.orderId &&
          !!payload.order.orderHeader.errorDetails
        );
      }),
      mergeMap(([payload, webSocketOrder]) => {
        const productNumbers = payload?.order?.orderItems?.map(orderItem => {
          return orderItem.productNumber;
        });
        return this.store
          .select(selectProductAttributesByIds(productNumbers))
          .pipe(
            take(1),
            map(productMap => {
              const websocketMsgContentLength =
                webSocketOrder?.orderTransactions.length;
              const newOrder =
                websocketMsgContentLength === 1
                  ? webSocketOrder?.orderTransactions[0]
                  : webSocketOrder?.orderTransactions[1];

              const webSocketOrderItems =
                this.orderItemDtoService.toClientModelArray(
                  newOrder?.orderItems,
                );
              const submittedOrderProducts =
                SharedTrackingService.tracingProductData(
                  payload?.order.orderItems,
                  productMap,
                );
              const webSocketOrderProducts =
                SharedTrackingService.tracingProductData(
                  webSocketOrderItems,
                  productMap,
                );
              const trace = JSON.parse(
                JSON.stringify(
                  submitExceptionsSuccessWithExceptionsWebSocketTrace,
                ),
              );
              trace.tracing.data.attributes.order.orderId =
                payload.order.orderHeader.orderId;
              trace.tracing.data.attributes.order.tandemOrderNumber =
                payload.order.orderHeader.tandemOrderNumber;
              trace.tracing.data.attributes.order.requestedDeliveryDate =
                newOrder.requestedDeliveryDate;
              trace.tracing.data.attributes.order.confirmedDeliveryDate =
                newOrder.confirmedDeliveryDate;
              trace.tracing.data.attributes.order.webSocketOrderProducts =
                webSocketOrderProducts;
              trace.tracing.data.attributes.order.submittedOrderProducts =
                submittedOrderProducts;
              trace.tracing.transformFunc = genericTracingTransformer;
              return trace;
            }),
          );
      }),
      switchMap(tracking => [
        ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptionsSuccessWithExceptionsWebSocket(
          {
            tracking,
          },
        ),
      ]),
    ),
  );

  submitOrderExceptionsFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ORDER_EXCEPTION_ACTIONS.resolveOrderFail),
      mergeMap(error => {
        return combineLatest([
          of(error),
          this.store.select(orderExceptionSelectors.selectOrderExceptionState),
        ]).pipe(take(1));
      }),
      switchMap(([error, exceptionState]) => {
        const trace = JSON.parse(JSON.stringify(submitExceptionsFailTrace));
        trace.tracing.data.isEndOfTrace = true;
        trace.tracing.data.isStartOfTrace = false;
        trace.tracing.data.attributes.order.orderId =
          exceptionState.order.orderHeader.orderId;
        trace.tracing.data.attributes.order.tandemOrderNumber =
          exceptionState.order.orderHeader.tandemOrderNumber;
        trace.tracing.transformFunc = genericTracingTransformer;
        return [
          ORDER_EXCEPTIONS_TRACING_ACTIONS.traceSubmitExceptionsFail({
            tracking: trace,
          }),
        ];
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private orderItemDtoService: OrderItemDTOService,
  ) {}
}
